import { Heading } from '@mm/components';
import { AngleRightLightIcon, IdeaIcon } from '@mm/icons';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useActor } from '@xstate/react';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../../../../common/components/TextInput';
import { colors } from '../../../../../../../../editor/features/beta/styles/colors';
import {
  SignUpFlowMachineService,
  SIGN_UP_FLOW_STATES_NAME,
} from '../../../../../../../signUpFlowMachine/signUpMachineTypes';
import { CompanyInfoSteps } from '../companyInfoStepBuilder';
import { VideoOnboarding } from '../VideoOnboarding/VideoOnboarding';
import { CheckButton } from './CheckButton/CheckButton';
import './QuestionComponent.scss';

type Props = {
  step: CompanyInfoSteps;
  service: SignUpFlowMachineService;
  setStep: (value: React.SetStateAction<number>) => void;
};

export const QuestionComponent = (props: Props) => {
  const { step, service, setStep } = props;
  const {
    question,
    buttons,
    icons,
    subtitle,
    subtitleHighlighted,
    onNext,
    inputPlaceholder,
    video,
    videoDescription,
    videoButton,
  } = step;
  const [current] = useActor(service);
  const { isOtherIndustrySelected } = current.context.stepOptions;
  const goal = current.context.company?.goal ?? '';
  const industry = current.context.company?.industry ?? '';
  const isGoalState = current.matches(SIGN_UP_FLOW_STATES_NAME.GOAL);
  const isIndustryState = current.matches(SIGN_UP_FLOW_STATES_NAME.INDUSTRY);
  const isVideoState = current.matches(SIGN_UP_FLOW_STATES_NAME.VIDEO);
  const isNewSignupFlow = useSelector(
    companyFeatureFlagsSelector,
  ).onboarding2022;
  const isInputVisible =
    (isOtherIndustrySelected && isIndustryState && inputPlaceholder) ||
    (isGoalState && isNewSignupFlow);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const value = (isGoalState && goal) || (isIndustryState && industry);
    setInputValue(value ? value : '');
  }, [isGoalState, goal, isIndustryState, industry]);

  const hasValue = inputValue.trim().length;

  const textInputClassName = classNames('question-component__text-input', {
    'question-component__text-input--disabled': !hasValue,
  });
  return (
    <div
      className={classNames({
        'question-component': !isVideoState,
        'question-component-goal': isGoalState,
        'question-component-industry': isIndustryState,
        'question-component-with-video': isVideoState,
      })}
    >
      {question && (
        <Heading
          size={1}
          text={question}
          className="question-component__heading"
        />
      )}
      {(subtitle || subtitleHighlighted) && (
        <div className="question-component__subtitle-container">
          <IdeaIcon className="question-component__subtitle-icon" />
          <div className="question-component__subtitle">
            <span className="question-component__subtitle--highlighted">
              {subtitleHighlighted}
            </span>{' '}
            {subtitle}
          </div>
        </div>
      )}
      {video && (
        <VideoOnboarding
          videoSrc={video}
          videoDescription={videoDescription}
          videoButton={videoButton}
        />
      )}

      {buttons && (
        <div className="question-component__buttons">
          {buttons.map((button, buttonIdx) => {
            return icons?.map((icon, iconIdx) => {
              return buttonIdx === iconIdx ? (
                <CheckButton
                  button={button}
                  key={button.text}
                  service={service}
                  setStep={setStep}
                  icon={icon}
                />
              ) : (
                <></>
              );
            });
          })}
          {isInputVisible && (
            <div className="question-component__text-input-container">
              <TextInput
                className={textInputClassName}
                value={inputValue}
                onChange={event => setInputValue(event.target.value)}
                autoComplete="false"
                placeholder={inputPlaceholder}
              />
              <PrimaryButton
                className="question-component__input-btn"
                disabled={isNewSignupFlow ? false : !hasValue}
                onClick={() => {
                  onNext?.(inputValue);
                  setStep(prev => prev + 1);
                }}
              >
                Next
                <AngleRightLightIcon
                  className="question-component__input-btn-icon"
                  color={
                    !hasValue && !isNewSignupFlow ? colors.darkGray : 'white'
                  }
                />
              </PrimaryButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
